import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Download, ShieldCheck, Thermometer } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Pagina pubblica (link firmato dal QR del registro temperature): Registro Anomalie Temperature del periodo, sola lettura
const RegistroAnomaliePage = () => {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const qs = new URLSearchParams(window.location.search);
  const params = { t: qs.get('t'), da: qs.get('da'), a: qs.get('a'), k: qs.get('k') };

  useEffect(() => {
    axios.get(`${API}/gestione/haccp/pubblico/registro-anomalie`, { params })
      .then(r => setDati(r.data))
      .catch(e => setErrore(e.response?.status === 403 ? 'Link non valido o scaduto.' : 'Registro non disponibile al momento.'));
  }, []);

  const pdfUrl = `${API}/gestione/haccp/pubblico/registro-anomalie/pdf?${new URLSearchParams(params).toString()}`;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="registro-anomalie-page">
      <header className="bg-red-900 text-white px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-red-200" />
          <div>
            <h1 className="text-base sm:text-lg font-bold leading-tight">Registro Anomalie Temperature HACCP</h1>
            {dati && <p className="text-xs text-red-100" data-testid="registro-anomalie-intestazione">{[dati.ragione_sociale, dati.partita_iva ? `P.IVA ${dati.partita_iva}` : '', dati.periodo].filter(Boolean).join(' · ')}</p>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5">
        {errore && <div data-testid="registro-anomalie-errore" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">{errore}</div>}
        {!dati && !errore && <p className="text-slate-500 text-sm">Caricamento…</p>}

        {dati && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span data-testid="registro-anomalie-totale"><b>{dati.totale}</b> non conformità nel periodo · {dati.frigoriferi} apparecchi monitorati</span>
              </div>
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="registro-anomalie-pdf-btn"
                 className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                <Download className="h-4 w-4" /> Scarica PDF
              </a>
            </div>

            {dati.anomalie.length === 0 ? (
              <div data-testid="registro-anomalie-vuoto" className="bg-white border border-slate-200 rounded-xl p-6 text-center text-slate-600">
                <Thermometer className="h-8 w-8 mx-auto mb-2 text-green-600" />
                Nessuna non conformità rilevata nel periodo selezionato.
              </div>
            ) : (
              <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
                <table className="w-full text-sm" data-testid="registro-anomalie-tabella">
                  <thead className="bg-red-900 text-white text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Data e ora</th>
                      <th className="px-3 py-2 text-left">Apparecchio</th>
                      <th className="px-3 py-2 text-center">T. rilevata</th>
                      <th className="px-3 py-2 text-center">T. prevista</th>
                      <th className="px-3 py-2 text-left">Origine</th>
                      <th className="px-3 py-2 text-left">Operatore</th>
                      <th className="px-3 py-2 text-left">Azione correttiva / Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.anomalie.map((a, i) => (
                      <tr key={a.id || i} data-testid={`anomalia-row-${i}`} className={i % 2 ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{a.data_ora}</td>
                        <td className="px-3 py-2 font-medium">{a.tipo === 'congelatore' ? '❄️ ' : '🧊 '}{a.apparecchio}</td>
                        <td className="px-3 py-2 text-center font-bold text-red-600">{a.temperatura != null ? `${a.temperatura}°C` : '—'}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{a.range}</td>
                        <td className="px-3 py-2 text-slate-600">{a.origine}</td>
                        <td className="px-3 py-2">{a.operatore}</td>
                        <td className="px-3 py-2">
                          {a.azione_correttiva ? <span className="text-green-800">{a.azione_correttiva}</span> : <span className="text-slate-400 italic">Nessuna azione registrata</span>}
                          {a.note && <div className="text-xs text-slate-500">Note: {a.note}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-4">Documento in sola lettura generato dal sistema HACCP Ristorantissimo — Reg. (CE) n. 852/2004.</p>
          </>
        )}
      </main>
    </div>
  );
};

export default RegistroAnomaliePage;
